/**
 * CombatEventVFXHandler — combat-event presentation beats, extracted from
 * VFXSystem (decomposition arc, 2026-06-12).
 *
 *  - `combat:damage`        → client-predicted hit VFX + FCT ('optimistic'
 *                             in the Visual Lifecycle digest) AND the
 *                             cast-interrupt check (damage during an
 *                             interruptible cast cancels it)
 *  - `ability:cast`         → cast glow + name (NOTE: this event currently
 *                             has no emitters — the live cast-VFX funnel is
 *                             `vfx:spawn` → spawnEffectFromData; kept until
 *                             the event is wired or formally retired)
 *  - `combat:knockback` / `combat:lifesteal` / `combat:bonus_damage` /
 *    `combat:mana_restore` / `combat:status_tick` → small one-shot beats
 *  - `combat:apply_effect`  → debuff routing into StatusEffectManager
 *
 * onItemPickup / onTeleport ride along as event-less public beats (no
 * emitter subscribes them today — wiring candidates).
 */
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { statusEffectManager } from '../../systems/combat/StatusEffectManager';
import { VisualLifecycleStore } from '../../diagnostics/VisualLifecycleStore';
import type { VFXEffectPlayer } from '../VFXEffectPlayer';
import { getAbilityVFX, getSchoolHitVFX } from '../config/AbilityVFXMap';
import { VFXEffectRegistry } from '../VFXEffectRegistry';
import { getAbilityData } from '../../../shared/content/generated/abilities.gen';
import { isThrowingStarAbility } from '../../../shared/combat/throwingStars';
import { resolveRemoteCastGlowId } from '../RemoteAbilityVFX';
import { CombatFeedbackManager } from '../CombatFeedbackManager';
import { resolveAbilityArchetype, archImpactId } from '../../../config/abilities/vfxArchetypes.config';
import { resolveEntityPosition, findMobPosition } from './entityPositions';
import { COMBAT_TEXT_BEATS } from '../../../config/abilities/combatTextBeats.config';
import { abilityDisplayName, fctAbilityLabel } from '../../systems/combat/fctLabel';
import { showsCastBar } from '../../systems/combat/castBarAbility';
import { resolveReaction } from '../../../config/abilities/reactionMatrix.config';
import { FCT_BEAT_STYLES, reactionLabelText, resolveFctStyle } from '../../../config/ui/fctStyle.config';
import { resolveDamageColor } from '../../../shared/config/combat-feedback.config';
import { getMotionPrefs } from '../../systems/accessibility/motionPrefs';
import { ActiveStatusIndex } from '../../systems/combat/ActiveStatusIndex';
import { reactivityPulse } from '../atmosphere/reactivityPulse';
import { REACTIVITY } from '../../../config/atmosphere/reactivity.config';
import { DecalLayer } from '../decals/DecalLayer';
import { groundMark } from '../decals/decalBuilders';
import { BloodwellExchangePresentation } from './BloodwellExchangePresentation';

export class CombatEventVFXHandler {
  constructor(private effectPlayer: VFXEffectPlayer) {}

  /** Install subscriptions via the owner's tracked `sub` helper. */
  register(sub: (event: string, handler: (data: any) => void) => void): void {
    // Eager-init the status index — its lazy first-query subscribe raced the
    // first wire status (index constructed at first HIT, after statuses flew).
    ActiveStatusIndex.getInstance();
    new BloodwellExchangePresentation(this.effectPlayer.floatingText).register(sub);
    sub('combat:damage', (data) => this.onDamageDealt(data));
    sub('ability:cast', (data) => this.onAbilityCast(data));

    // Combat modifier events (visual feedback)
    sub('combat:knockback', (data) => this.onKnockback(data));
    sub('combat:lifesteal', (data) => this.onLifesteal(data));
    sub('combat:bonus_damage', (data) => this.onBonusDamage(data));
    sub('combat:mana_restore', (data) => this.onManaRestore(data));

    // Status effect / debuff routing through StatusEffectManager
    sub('combat:apply_effect', (data) => this.onApplyEffect(data));

    // Cast interruption: damage during interruptible cast cancels it
    sub('combat:damage', (data) => this.onCasterDamaged(data));

    // Status tick damage (from StatusEffectManager)
    sub('combat:status_tick', (data) => this.onStatusTick(data));
  }

  // ── FCT helpers (shared by the beats below + VFXSystem's public proxies) ──
  //
  // Phase 2 (FCT choreography spec integration 8): beat texts resolve the
  // SAME style grammar as the confirmed numbers and record fct_* lifecycle
  // events (they were invisible to JSON-verdict QA before — spec §1.4 gap).
  // The `school` field is deliberately left null on beat records: the
  // confirmed number for the same hit records fct_damage keyed on
  // target+ability+school, and sharing that key would false-flag the beat
  // as a cross-path duplicate. School (when known) rides `meta`.

  showDamageNumber(
    amount: number, x: number, y: number, isCritical: boolean = false,
    beat?: { beat: string; targetId?: string; tick?: boolean },
  ): void {
    if (isCritical) {
      this.effectPlayer.playById('hit_critical', x, y, { amount });
      return;
    }
    this.effectPlayer.playById('hit_physical', x, y, { amount });
    const style = resolveFctStyle({
      amount,
      school: 'physical',
      flags: { tick: beat?.tick, local: true },
      colorblindSafe: getMotionPrefs().colorblindSafe,
    });
    VisualLifecycleStore.record({
      type: 'fct_damage',
      label: `${amount} (${beat?.beat ?? 'local'})`,
      x, y,
      targetId: beat?.targetId ?? null,
      source: 'beat',
      meta: { beat: beat?.beat ?? 'local', damage: amount, tier: style.tier, verb: style.verb },
    });
    this.effectPlayer.floatingText.showDamageNumber(amount, x, y, false, {
      tick: beat?.tick,
      targetKey: beat?.targetId,
    });
  }

  showHealingNumber(amount: number, x: number, y: number, beat: string = 'heal'): void {
    this.effectPlayer.playById('hit_heal', x, y, { amount });
    const style = resolveFctStyle({
      amount,
      school: 'physical',
      flags: { heal: true, local: true },
      colorblindSafe: getMotionPrefs().colorblindSafe,
    });
    VisualLifecycleStore.record({
      type: 'fct_heal',
      label: `+${amount} (${beat})`,
      x, y,
      source: 'beat',
      meta: { beat, amount, tier: style.tier, verb: style.verb },
    });
    this.effectPlayer.floatingText.showHealingNumber(amount, x, y);
  }

  showAbilityName(abilityName: string, x: number, y: number): void {
    this.effectPlayer.floatingText.showAbilityName(abilityName, x, y);
  }

  // ── Primary beats ──────────────────────────────────────────────────

  onDamageDealt(data: { defender: any; damage: number; damageType?: string; isCritical?: boolean; abilityId?: string }): void {
    const pos = resolveEntityPosition(data.defender);
    if (!pos) return;
    const { x, y } = pos;
    const targetId = data.defender?.id;

    const mapping = data.abilityId ? getAbilityVFX(data.abilityId) : undefined;
    // Authored impact channel (VQA sprint U-vfx-authority-1): TOML
    // `impact_effect` compiles to vfx.impactEffect but had no reader on this
    // path — authored impacts never rendered and, since combat:damage
    // emitters omit damageType, every unmapped ability degraded to the
    // school-blind physical flash. Resolve explicit map → authored TOML →
    // school fallback, deriving school from the ability when absent.
    const authored = data.abilityId ? getAbilityData(data.abilityId) : undefined;
    const school = data.damageType || authored?.school || 'physical';
    // Resolution-order doctrine (2026-07-17 wave-2): the authored TOML
    // impactEffect OUTRANKS the curated map — 189 of 191 dual-defined
    // abilities diverged, and the TOML names are uniformly richer
    // (fire_pillar/frost_prison/polymorph_poof vs generic hit_*). The map
    // is the fallback tier for abilities without authored impacts.
    const authoredImpactId = [authored?.vfx?.impactEffect, mapping?.hitVfxId]
      .find((id): id is string => !!id && VFXEffectRegistry.has(id));
    // A crit must not ERASE an authored signature under the generic bloom
    // (eviscerate FAIL, session #21 + sprint review): authored impacts win.
    // U-vfx-authority-5 (impact half): the archetype impact tier
    // (arch_impact_<cls>_<school>, registered at boot) previously had no
    // playback path — unauthored, unmapped hits fell straight to the generic
    // school flash. Prefer the family-coherent impact when one exists.
    // Crit composition (2026-07-17): crits no longer hijack this resolution —
    // the school/authored hit always plays, and `crit_overlay` (gold star
    // ring + shake, paletteless) stacks ON TOP below, so a nature crit stays
    // green instead of collapsing into the generic gold bloom.
    const archetype = authored ? resolveAbilityArchetype(authored.class, String(school)) : undefined;
    const archImpact = archetype ? archImpactId(archetype.cls, archetype.school) : undefined;
    const hitEffectId = authoredImpactId
      || (archImpact && VFXEffectRegistry.has(archImpact) ? archImpact : undefined)
      || getSchoolHitVFX(school);

    VisualLifecycleStore.record({
      type: 'hit_vfx',
      label: `${hitEffectId} dmg=${data.damage}${data.isCritical ? ' CRIT' : ''}`,
      x, y,
      abilityId: data.abilityId || null,
      targetId: targetId || null,
      // The derived school (damageType → ability TOML → physical), not the
      // raw wire field — combat:damage emitters omit damageType, which made
      // this sidecar say 'physical' for a correct hit_nature (the K-item).
      school,
      // Client-predicted path (combat:damage). The damage here is the
      // client's prediction — it can disagree with the server-confirmed
      // FCT number; the 'optimistic' tag makes that visible in the digest.
      source: 'optimistic',
      meta: { effectId: hitEffectId, damage: data.damage, isCritical: !!data.isCritical, source: 'onDamageDealt' },
    });

    this.effectPlayer.playById(hitEffectId, x, y, {
      amount: data.damage,
      targetId,
      isCritical: !!data.isCritical,
      followOffset: data.defender?.followOffset, // a projectile's contact point stays on the body there
    });
    // Crit composition: stack the paletteless gold overlay on the school hit.
    // Skipped when the authored impact IS the classic bloom (execute et al.)
    // — those already carry the full crit grammar including the shake.
    if (data.isCritical && hitEffectId !== 'hit_critical') {
      this.effectPlayer.playById('crit_overlay', x, y, { targetId });
    }
    // Register the play so the server-confirmed impact (DamagePresentation-
    // Service → triggerImpact) skips its duplicate effect for this hit.
    CombatFeedbackManager.getInstance().noteOptimisticImpact(targetId, data.abilityId);

    // Aftermath decal (ground-marks spec §4.3 derivation ladder). Rungs 1+2
    // are the AUTHORED compile channel (TOML `ground_mark` → vfx.groundMark):
    // 'none' opts the ability out entirely; a family name forces that mark
    // for ANY ability, including single-target opt-ins. Rung 3 is the auto
    // ladder — ground-committed impacts (target='ground'|'aoe') only, so
    // single-target hits don't stipple the floor; SCHOOL_MARK_FAMILY picks
    // the family. place() owns the ground_mark lifecycle record — no
    // double-recording here.
    const authoredMark = authored?.vfx?.groundMark;
    if (authoredMark && authoredMark !== 'none') {
      DecalLayer.getInstance().place(
        groundMark({ school: String(school), family: authoredMark, ttlMs: authored?.vfx?.groundMarkTtlMs }),
        x, y + 18,
        { abilityId: data.abilityId ?? null, targetId: targetId ?? null, source: 'impact' },
      );
    } else if (authoredMark !== 'none' && (authored?.target === 'ground' || authored?.target === 'aoe')) {
      DecalLayer.getInstance().place(
        groundMark({ school: String(school) }),
        x, y + 18,
        { abilityId: data.abilityId ?? null, targetId: targetId ?? null, source: 'impact' },
      );
    }

    // World-combat reactivity (fx-grammar spec E1): a heavy landed hit pulses
    // the atmosphere at the impact POINT — fireflies scatter, ambient glow
    // flares. Bus-emitted (no atmosphere import chain); the driver gates it
    // (budget / cooldown-merge / flash ceiling). Purely presentational.
    if (data.isCritical || data.damage >= REACTIVITY.heavyImpactDamageThreshold) {
      const impactIntensity = Math.min(1, Math.max(0.3, data.damage / REACTIVITY.heavyImpactDamageNorm));
      reactivityPulse('heavyImpact', { x, y }, impactIntensity, String(school));
    }

    // Interaction layer (wave-2): status-on-target × incoming school →
    // reaction beat ON TOP of the hit (frozen shatters under physical, burn
    // steams under frost — reactionMatrix.config.ts). Purely presentational;
    // recorded as reaction_vfx so QA reads reactions from JSON.
    if (targetId) {
      // Union of local prediction (statuses THIS client applied) and the
      // wire-truth index (statuses ANYONE applied) — cross-player reactions
      // (A freezes, B shatters) work for every observer.
      const statuses = [...new Set([
        ...statusEffectManager.getEffects(targetId).map((e) => e.effectId),
        ...ActiveStatusIndex.getInstance().getStatuses(targetId),
      ])];
      const reaction = statuses.length ? resolveReaction(statuses, String(school)) : null;
      if (reaction && VFXEffectRegistry.has(reaction.effectId)) {
        VisualLifecycleStore.record({
          type: 'reaction_vfx',
          label: `${reaction.effectId} (${reaction.note})`,
          x, y,
          abilityId: data.abilityId || null,
          targetId,
          school,
          meta: { effectId: reaction.effectId, statuses, school },
        });
        this.effectPlayer.playById(reaction.effectId, x, y, { targetId });
        // World-combat reactivity (fx-grammar spec E2): the reaction beat also
        // pulses the scene (ambient glow flashes toward the reaction school).
        reactivityPulse('reactionBeat', { x, y }, 0.7, String(school));
        // Reaction aftermath (ground-marks spec §2.4): the battlefield keeps
        // the story — a shatter leaves ice shards, a quench leaves a wet stain.
        if (reaction.aftermath) {
          const a = reaction.aftermath;
          DecalLayer.getInstance().place(
            groundMark({ school: a.school, family: a.family, size: a.size, ttlMs: a.ttlMs }),
            x, y + 18,
            { abilityId: data.abilityId ?? null, targetId, source: 'reaction' },
          );
        }
        // Reaction word beat (FCT spec §2.d + Phase 2 integration 10): a
        // small caps label ("SHATTER") above the number lane, spawned from
        // THIS resolved-reaction branch only — the same guard that played
        // the effect. Honest visuals: no reaction fired ⇒ no word, ever.
        const labelStyle = FCT_BEAT_STYLES.reaction_label;
        const word = reactionLabelText(reaction.effectId);
        VisualLifecycleStore.record({
          type: 'fct_damage',
          label: word,
          x, y,
          abilityId: data.abilityId || null,
          targetId,
          // school stays null (rides meta): the confirmed number for this
          // same hit keys fct_damage on target+ability+school — sharing the
          // key would false-flag the label as a cross-path duplicate.
          source: 'beat',
          meta: { beat: 'reaction_label', effectId: reaction.effectId, school, verb: labelStyle.verb },
        });
        this.effectPlayer.floatingText.showFloatingText(word, x, y, {
          text: word,
          color: resolveDamageColor(String(school), false, getMotionPrefs().colorblindSafe),
          fontSize: `${labelStyle.fontSize}px`,
          duration: labelStyle.durationMs ?? 900,
          offsetY: labelStyle.offsetY ?? 44,
          verb: labelStyle.verb,
          alwaysRender: true,
        });
      }
    }
  }

  onAbilityCast(data: { caster: any; abilityId: string; abilityType?: string; target?: any }): void {
    const pos = resolveEntityPosition(data.caster);
    if (!pos) return;
    const { x, y } = pos;
    const casterId = data.caster?.id;

    // A cast-bar ability's name is on the bar already (plan 15 §2.5).
    if (data.abilityId !== 'assassin_auto' && !showsCastBar(data.abilityId)) this.showAbilityName(abilityDisplayName(data.abilityId), x, y + COMBAT_TEXT_BEATS.abilityNameOffsetY);
    // Projectile launch/contact owns the physical star's release and impact.
    if (isThrowingStarAbility(data.abilityId)) return;

    // Use ability-specific cast VFX if available. U-vfx-authority-5 (local
    // half): this used to pass data.abilityType ('offensive'/'utility' — not
    // a school!) into getSchoolHitVFX, so every unmapped local cast glowed
    // generic physical. Resolve the same chain the remote glow uses — TOML
    // cast_effect → map castVfxId → archetype → school (cast-side doctrine
    // flip 2026-07-17, authored-first); skip the authored cast_effect tier
    // when a wind-up exists (the wind-up branch already renders it — playing
    // it twice doubles the visual).
    const mapping = getAbilityVFX(data.abilityId);
    const castToml = getAbilityData(data.abilityId);
    const castEffectId = resolveRemoteCastGlowId(data.abilityId, undefined, {
      omitAuthoredCastEffect: (castToml?.windUpMs ?? 0) > 0,
    });
    VisualLifecycleStore.record({
      type: 'ability_cast_vfx',
      label: `${data.abilityId} → ${castEffectId}`,
      x, y,
      abilityId: data.abilityId,
      meta: { effectId: castEffectId, origin: 'local' },
    });
    this.effectPlayer.playById(castEffectId, x, y, { targetId: casterId });

    // If there's a target, also play the hit VFX at the target position
    if (data.target && mapping?.hitVfxId) {
      const targetPos = resolveEntityPosition(data.target);
      if (targetPos) {
        this.effectPlayer.playById(mapping.hitVfxId, targetPos.x, targetPos.y, { targetId: data.target?.id });
      }
    }
  }

  onItemPickup(data: { item: any; player: any }): void {
    const x = data.item?.x || data.player?.x || 0;
    const y = data.item?.y || data.player?.y || 0;
    this.effectPlayer.playById('item_pickup', x, y);
  }

  onTeleport(data: { player: any; teleporter: any }): void {
    const x = data.teleporter?.x || data.player?.x || 0;
    const y = data.teleporter?.y || data.player?.y || 0;
    this.effectPlayer.playById('teleport_flash', x, y);
  }

  // ── Combat modifier beats ──────────────────────────────────────────

  private onKnockback(data: { targetId: string; attackerId: string; distance: number; x: number; y: number }): void {
    // Visual knockback indicator — small directional burst
    this.effectPlayer.playById('hit_physical', data.x, data.y, { scale: COMBAT_TEXT_BEATS.knockbackBurstScale });
  }

  private onLifesteal(data: { attackerId: string; amount: number }): void {
    // Find attacker position and show heal number
    try {
      const entitySystem = ServiceRegistry.getInstance().get('EntitySystem');
      const player = entitySystem?.getPlayerManager?.()?.getPlayer?.();
      if (player && player.id === data.attackerId) {
        const x = player.position?.x ?? 0;
        const y = player.position?.y ?? 0;
        this.showHealingNumber(data.amount, x, y + COMBAT_TEXT_BEATS.lifestealOffsetY, 'lifesteal');
      }
    } catch { /* non-critical */ }
  }

  private onBonusDamage(data: { targetId: string; attackerId: string; damage: number }): void {
    const pos = findMobPosition(data.targetId);
    if (pos) {
      const off = COMBAT_TEXT_BEATS.bonusDamageOffset;
      this.showDamageNumber(data.damage, pos.x + off.x, pos.y + off.y, false, {
        beat: 'bonus_damage',
        targetId: data.targetId,
      });
    }
  }

  private onManaRestore(data: { playerId: string; amount: number }): void {
    try {
      const entitySystem = ServiceRegistry.getInstance().get('EntitySystem');
      const player = entitySystem?.getPlayerManager?.()?.getPlayer?.();
      if (player) {
        const x = player.position?.x ?? 0;
        const y = player.position?.y ?? 0;
        const beat = COMBAT_TEXT_BEATS.manaRestore;
        // Mana is deliberately un-punchy (spec §2.c) — drift verb, copy unchanged.
        VisualLifecycleStore.record({
          type: 'fct_heal',
          label: `+${data.amount} MP`,
          x, y: y + beat.offsetY,
          source: 'beat',
          meta: { beat: 'mana_restore', amount: data.amount, verb: FCT_BEAT_STYLES.mana_restore.verb },
        });
        this.effectPlayer.floatingText.showFloatingText(`+${data.amount} MP`, x, y + beat.offsetY, {
          text: `+${data.amount} MP`,
          color: beat.color,
          fontSize: beat.fontSize,
          duration: beat.durationMs,
          offsetY: beat.offsetY,
          verb: FCT_BEAT_STYLES.mana_restore.verb,
        });
      }
    } catch { /* non-critical */ }
  }

  // ── Wiring beats ───────────────────────────────────────────────────

  private onApplyEffect(data: any): void {
    // Route debuff effects through StatusEffectManager for stacking support
    if (data.effect?.type === 'debuff' && data.targetId) {
      const defaults = COMBAT_TEXT_BEATS.debuffDefaults;
      statusEffectManager.apply({
        effectId: data.effect.stat || 'unknown',
        targetId: data.targetId,
        sourceId: data.casterId || '',
        duration: data.effect.duration || defaults.durationMs,
        tickDamage: data.effect.value || 0,
        tickRateMs: defaults.tickRateMs,
        maxStacks: defaults.maxStacks,
      });
    }
  }

  private onCasterDamaged(data: { defender?: any; targetId?: string }): void {
    // If the damage target has an active interruptible cast, interrupt it
    const targetId = data.targetId || data.defender?.id;
    if (!targetId) return;
    try {
      const castTimeline = ServiceRegistry.getInstance().get('CastTimelineSystem');
      if (castTimeline?.tryInterrupt(targetId)) {
        // Show interrupt VFX
        const pos = findMobPosition(targetId);
        if (pos) {
          const beat = COMBAT_TEXT_BEATS.interrupted;
          // Interrupt migrates onto verb `pop` unchanged in copy (spec §2.d).
          VisualLifecycleStore.record({
            type: 'fct_damage',
            label: beat.text,
            x: pos.x, y: pos.y + beat.offsetY,
            targetId,
            source: 'beat',
            meta: { beat: 'interrupted', verb: FCT_BEAT_STYLES.interrupted.verb },
          });
          this.effectPlayer.floatingText.showFloatingText(beat.text, pos.x, pos.y + beat.offsetY, {
            text: beat.text, color: beat.color, fontSize: beat.fontSize, duration: beat.durationMs, offsetY: beat.offsetY,
            verb: FCT_BEAT_STYLES.interrupted.verb,
            alwaysRender: true,
          });
        }
      }
    } catch { /* non-critical */ }
  }

  private onStatusTick(data: { targetId: string; effectId: string; damage: number; stacks: number }): void {
    const pos = findMobPosition(data.targetId);
    if (pos) {
      // Local-tick numbers pin tier 0 `drift` (spec §2.d — deliberately
      // un-punchy) via the tick flag.
      this.showDamageNumber(data.damage, pos.x, pos.y + COMBAT_TEXT_BEATS.statusTickOffsetY, false, {
        beat: 'status_tick',
        targetId: data.targetId,
        tick: true,
      });
      // Attribute the tick to its DoT — the capture review's "30 then 22"
      // fireball frames were unreadable without source labels (2026-06-12).
      const label = fctAbilityLabel(data.effectId, { tick: true });
      if (label) {
        const beat = COMBAT_TEXT_BEATS.tickLabel;
        const y = pos.y + COMBAT_TEXT_BEATS.statusTickOffsetY + beat.extraOffsetY;
        this.effectPlayer.floatingText.showFloatingText(label, pos.x, y, {
          text: label,
          color: beat.color,
          fontSize: beat.fontSize,
          duration: beat.durationMs,
          offsetY: COMBAT_TEXT_BEATS.statusTickOffsetY + beat.extraOffsetY,
          verb: FCT_BEAT_STYLES.tick_label.verb,
          // Companion of the tick number: no slot alloc, no cap accounting.
          companion: true,
        });
      }
    }
  }
}
